import { useEffect, useState } from "react";
import api, { money } from "../../lib/api";
import { PageHeader } from "../../components/PortalShell";
import { Donut } from "../../components/Sparkline";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { CreditCard, Plus, Snowflake, Download, Calendar } from "lucide-react";
import { toast } from "sonner";

export default function BuyerCapital() {
  const [summary, setSummary] = useState(null);
  const [apps, setApps] = useState([]);
  const [cards, setCards] = useState([]);
  const [statements, setStatements] = useState([]);
  const [amount, setAmount] = useState(15000);
  const [terms, setTerms] = useState("net60");
  const [cardOpen, setCardOpen] = useState(false);
  const [cardForm, setCardForm] = useState({ label: "", limit: 5000 });

  const load = () => Promise.all([
    api.get("/capital/summary"),
    api.get("/credit/applications"),
    api.get("/cards"),
    api.get("/statements"),
  ]).then(([s,a,c,st])=>{setSummary(s.data); setApps(a.data); setCards(c.data); setStatements(st.data);});
  useEffect(() => { load(); }, []);

  const apply = async () => {
    const { data } = await api.post("/credit/apply", { amount: Number(amount), terms });
    toast.success(data.status === "approved" ? `Approved at ${(data.rate*100).toFixed(2)}% — funds unlocked` : "Submitted — pending review");
    load();
  };

  const issueCard = async () => {
    if (!cardForm.label) return toast.error("Add a label");
    const { data } = await api.post("/cards", { label: cardForm.label, limit: Number(cardForm.limit) });
    toast.success(`Card issued · ${data.pan_masked}`);
    setCardOpen(false); setCardForm({ label: "", limit: 5000 });
    load();
  };

  const freeze = async (id) => { await api.patch(`/cards/${id}/freeze`); toast.success("Card frozen"); load(); };

  if (!summary) return <div className="p-8 text-foreground/60">Loading…</div>;

  return (
    <>
      <PageHeader title="Capital & Credit" subtitle="Embedded financing, virtual cards, statements"/>
      <div className="p-8 space-y-6">

        {/* AI auto-grant banner — shows when buyer has been pre-approved */}
        {summary.ai_grant?.active && (
          <div className="rounded-2xl border border-emerald-200 bg-gradient-to-r from-emerald-50 via-emerald-50 to-white p-5 flex items-center gap-4" data-testid="ai-grant-banner">
            <div className="w-12 h-12 rounded-xl bg-emerald-600 grid place-items-center shrink-0">
              <span className="text-white text-xl">✨</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="display text-base text-emerald-900">You're pre-approved for {money(summary.ai_grant.limit_usd)}</span>
                <span className="chip text-[10px] mono uppercase tracking-widest bg-emerald-600 text-white">
                  {summary.ai_grant.terms?.replace("net", "Net-") || "—"}
                </span>
                <span className="text-[10px] mono uppercase tracking-widest text-emerald-900/60">
                  {Math.round((summary.ai_grant.confidence || 0) * 100)}% AI confidence
                </span>
              </div>
              <div className="text-[12.5px] text-emerald-900/70 mt-0.5">
                Auto-approved by our AI underwriter based on your verified business signals. Use it on any order.
              </div>
            </div>
          </div>
        )}

        {/* Top: utilization */}
        <div className="grid lg:grid-cols-3 gap-5">
          <div className="card-ink rounded-2xl p-7 relative overflow-hidden" data-testid="cap-summary">
            <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-accent/20 blur-3xl"/>
            <div className="relative flex items-center gap-6">
              <Donut value={summary.utilization_pct} max={100} size={120} stroke={12} color="#E8FF3A" track="rgba(232,255,58,0.15)"/>
              <div>
                <div className="text-xs mono uppercase tracking-widest text-accent/80">Available</div>
                <div className="display text-4xl mt-1">{money(summary.available)}</div>
                <div className="text-xs text-background/60 mt-1">of {money(summary.credit_limit)} limit</div>
                <div className="mt-3 chip bg-accent/30 border border-accent/40 text-background">{summary.utilization_pct}% utilized</div>
              </div>
            </div>
            <div className="relative mt-7 grid grid-cols-2 gap-3 text-xs">
              <div><div className="text-background/50">Outstanding</div><div className="display text-base">{money(summary.outstanding)}</div></div>
              <div><div className="text-background/50">Lifetime drawn</div><div className="display text-base">{money(summary.drawn_lifetime)}</div></div>
              <div><div className="text-background/50">Rate</div><div className="display text-base">{(summary.rate*100).toFixed(2)}%</div></div>
              <div><div className="text-background/50">Facilities</div><div className="display text-base">{summary.approved_facilities}</div></div>
            </div>
          </div>

          {/* Apply card */}
          <div className="rounded-2xl bg-white border border-border p-6 lg:col-span-2">
            <div className="flex items-center gap-2 mb-3"><CreditCard className="w-5 h-5"/><h2 className="display text-lg">Apply for credit</h2></div>
            <p className="text-sm text-foreground/60 mb-5">Score-powered underwriting · 30 second decision</p>
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="sm:col-span-2">
                <Label>Amount</Label>
                <Input type="number" value={amount} onChange={e=>setAmount(e.target.value)} className="mt-1.5 h-11" data-testid="cap-amount"/>
                <div className="mt-2 grid grid-cols-3 gap-1.5">
                  {[5000, 15000, 30000].map(p => (
                    <button key={p} onClick={()=>setAmount(p)} className="text-xs rounded-md py-1.5 border border-border hover:bg-secondary">{money(p)}</button>
                  ))}
                </div>
              </div>
              <div>
                <Label>Terms</Label>
                <div className="grid grid-cols-3 gap-1.5 mt-1.5">
                  {["net30","net60","net90"].map(t => (
                    <button key={t} onClick={()=>setTerms(t)} data-testid={`cap-term-${t}`} className={`rounded-lg py-2.5 text-xs border ${terms===t?"bg-foreground text-background border-foreground":"bg-white border-border"}`}>{t.toUpperCase()}</button>
                  ))}
                </div>
              </div>
            </div>
            <Button onClick={apply} data-testid="cap-apply" className="mt-5 w-full rounded-full bg-foreground text-background hover:bg-foreground/90 h-11">
              Apply — instant decision · est. {((summary.rate)*100).toFixed(2)}% APR
            </Button>
          </div>
        </div>

        {/* Virtual cards */}
        <div className="rounded-2xl bg-white border border-border p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="display text-lg flex items-center gap-2"><CreditCard className="w-4 h-4"/>Virtual cards</h2>
              <p className="text-xs text-foreground/60 mt-0.5">Issue per-supplier cards with custom spend limits</p>
            </div>
            <Dialog open={cardOpen} onOpenChange={setCardOpen}>
              <DialogTrigger asChild>
                <Button data-testid="issue-card-btn" className="rounded-full bg-foreground text-background hover:bg-foreground/90"><Plus className="w-4 h-4 mr-1"/>Issue card</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Issue a virtual card</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Label (e.g. "GlowCo POs")</Label><Input value={cardForm.label} onChange={e=>setCardForm({...cardForm,label:e.target.value})} data-testid="card-label"/></div>
                  <div><Label>Spend limit</Label><Input type="number" value={cardForm.limit} onChange={e=>setCardForm({...cardForm,limit:e.target.value})}/></div>
                  <Button onClick={issueCard} data-testid="card-submit" className="w-full rounded-full bg-foreground text-background hover:bg-foreground/90">Issue card</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {cards.map(c => (
              <div key={c.id} className="rounded-2xl bg-foreground text-background p-5 relative overflow-hidden" data-testid={`card-${c.id.slice(0,6)}`}>
                <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-accent/20 blur-2xl"/>
                <div className="relative flex items-start justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-accent/70">{c.label}</div>
                    {c.seller_lock && <div className="text-[10px] text-background/60 mt-0.5">Locked to {c.seller_lock}</div>}
                  </div>
                  <div className="text-xs mono text-background/50">VISA</div>
                </div>
                <div className="relative mt-7 mono text-lg tracking-wider">{c.pan_masked}</div>
                <div className="relative mt-4 grid grid-cols-3 gap-2 text-[10px]">
                  <div><div className="text-background/50 uppercase">Exp</div><div className="mono">{c.expiry}</div></div>
                  <div><div className="text-background/50 uppercase">CVV</div><div className="mono">•••</div></div>
                  <div><div className="text-background/50 uppercase">Status</div><div className={c.status==="active"?"text-accent":"text-rose-300"}>{c.status}</div></div>
                </div>
                <div className="relative mt-5 h-1 bg-background/10 rounded-full overflow-hidden">
                  <div className="h-full bg-accent" style={{width: `${(c.spent/c.limit)*100}%`}}/>
                </div>
                <div className="relative mt-1.5 flex justify-between text-[10px] text-background/60">
                  <span>{money(c.spent)} spent</span><span>{money(c.limit)} limit</span>
                </div>
                {c.status === "active" && (
                  <button onClick={()=>freeze(c.id)} className="relative mt-4 w-full text-xs bg-background/10 hover:bg-background/20 rounded-md py-1.5 inline-flex items-center justify-center gap-1.5">
                    <Snowflake className="w-3 h-3"/>Freeze card
                  </button>
                )}
              </div>
            ))}
            {cards.length === 0 && <div className="col-span-full text-center py-10 text-foreground/60 text-sm">No virtual cards yet. Issue one for a specific supplier.</div>}
          </div>
        </div>

        {/* Repayment + statements */}
        <div className="grid lg:grid-cols-2 gap-5">
          <div className="rounded-2xl bg-white border border-border p-6">
            <h2 className="display text-lg flex items-center gap-2 mb-4"><Calendar className="w-4 h-4"/>Upcoming payments</h2>
            <div className="divide-y divide-border">
              {summary.next_payments.map((p,i)=>(
                <div key={i} className="py-3 flex justify-between items-center text-sm">
                  <div>
                    <div className="font-medium">{p.code}</div>
                    <div className="text-xs text-foreground/60">{p.seller} · due {new Date(p.date).toLocaleDateString()}</div>
                  </div>
                  <div className="display">{money(p.amount)}</div>
                </div>
              ))}
              {summary.next_payments.length===0 && <div className="text-center py-8 text-foreground/60 text-sm">No upcoming payments 🎉</div>}
            </div>
          </div>

          <div className="rounded-2xl bg-white border border-border p-6">
            <h2 className="display text-lg mb-4">Statements</h2>
            <div className="divide-y divide-border">
              {statements.map(s => (
                <div key={s.id} className="py-3 flex justify-between items-center text-sm">
                  <div>
                    <div className="font-medium">{s.period}</div>
                    <div className="text-xs text-foreground/60">{s.invoices} invoices · {money(s.total)}</div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={()=>toast.info("PDF export coming soon")}><Download className="w-3.5 h-3.5"/></Button>
                </div>
              ))}
              {statements.length===0 && <div className="text-center py-8 text-foreground/60 text-sm">No statements yet</div>}
            </div>
          </div>
        </div>

        {/* Applications history */}
        <div className="rounded-2xl bg-white border border-border p-6">
          <h2 className="display text-lg mb-4">Credit applications</h2>
          <div className="divide-y divide-border">
            {apps.map(a => (
              <div key={a.id} className="py-3 grid grid-cols-4 items-center text-sm">
                <span className="display">{money(a.amount)}</span>
                <span className="uppercase text-xs">{a.terms}</span>
                <span className="mono text-foreground/60">{(a.rate*100).toFixed(2)}%</span>
                <span className={`justify-self-end chip ${a.status==="approved"?"bg-accent/30 border border-accent/40":a.status==="pending"?"bg-secondary":"bg-destructive/10 text-destructive"}`}>{a.status}</span>
              </div>
            ))}
            {apps.length===0 && <div className="text-center py-8 text-foreground/60 text-sm">No applications yet</div>}
          </div>
        </div>
      </div>
    </>
  );
}
